import { FC, memo, useMemo } from 'react';
import { BarChart, Bar, XAxis, Legend, ResponsiveContainer, Tooltip } from 'recharts';

import { grayBase, grayPurple, white } from '../../../constants';
import { Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getCircadianPhaseTitle, getWeekdayLabels } from '../../../i18n';

type WeekBarChartItem = { day: string } & Record<string, number>;

interface DataWeeklyChartData {
  data: WeekBarChartItem[];
  keys: string[];
  colors: string[];
  labels: string[];
}

interface DataWeeklyChartProps {
  zoneData?: DataWeeklyChartData;
  labelData?: DataWeeklyChartData;
  chartViewMode: 'label' | 'zone';
  hideLegend?: boolean;
}

const DataWeeklyChart: FC<DataWeeklyChartProps> = ({
  zoneData,
  labelData,
  chartViewMode,
  hideLegend = false,
}) => {
  const { t } = useTranslation();
  const dayNames = useMemo(() => getWeekdayLabels(t, 'short'), [t]);
  const currentData = useMemo(() => {
    if (chartViewMode === 'zone' && zoneData) {
      return zoneData;
    }

    if (chartViewMode === 'label' && labelData) {
      return labelData;
    }

    return {
      data: [],
      keys: [],
      colors: [],
      labels: [],
    };
  }, [zoneData, labelData, chartViewMode]);

  const chartData = useMemo(() => {
    if (!currentData.data) return [];
    return currentData.data.map((item, index) => ({
      dayName: dayNames[index],
      ...item,
    }));
  }, [currentData.data, dayNames]);

  const activeKeys = useMemo(() => {
    if (!currentData.data) return [];
    return currentData.keys.filter((key) => currentData.data.some((day) => day[key] > 0));
  }, [currentData.keys, currentData.data]);

  const keyColorMap = useMemo(() => {
    if (!currentData.keys.length) return {};
    const map: Record<string, string> = {};
    currentData.keys.forEach((key, index) => {
      map[key] = currentData.colors[index];
    });
    return map;
  }, [currentData.keys, currentData.colors]);

  const renderCustomLabel = (props) => {
    const { x, y, width, index } = props;

    const dayData = chartData[index];
    if (!dayData) return null;

    const total = Object.keys(dayData)
      .filter((key) => key !== 'dayName' && key !== 'day' && typeof dayData[key] === 'number')
      .reduce((sum, key) => sum + (dayData[key] || 0), 0);

    if (total === 0) return null;

    return (
      <text x={x + width / 2} y={y - 5} textAnchor="middle" fill={grayBase} fontSize="12">
        {total}
      </text>
    );
  };

  const renderLegend = (props) => {
    const { payload } = props;
    if (hideLegend) return null;

    return (
      <div className="week-task-chart__legends">
        {payload.map((entry: { color: string; value: string }, index: number) => (
          <div
            className="week-task-chart__legend"
            key={`item-${index}`}
            style={{ border: `1px solid ${entry.color}` }}
          >
            <div
              className="week-task-chart__legend-color"
              style={{ backgroundColor: entry.color }}
            />
            <Typography variant="caption">{entry.value}</Typography>
          </div>
        ))}
      </div>
    );
  };

  const renderTooltip = (props) => {
    const { active, payload, label } = props;
    if (!active || !payload || !payload.length) return null;
    const total = payload.reduce((sum, entry) => sum + (entry.value || 0), 0);
    return (
      <Stack sx={{ background: 'white', padding: '8px', borderRadius: '8px' }}>
        <Typography variant="subtitle2" mb={0.5}>
          {t('data.task.doneTasksWithCount', { count: total })}
        </Typography>
        {payload.map((entry, index) => (
          <Typography key={index} variant="caption" style={{ color: entry.color }}>
            {entry.name}: {entry.value}
          </Typography>
        ))}
      </Stack>
    );
  };

  const getSeriesName = (key: string) => {
    if (chartViewMode === 'zone') {
      return getCircadianPhaseTitle(t, key, key);
    }

    return key;
  };

  return (
    <ResponsiveContainer width="100%" height={193}>
      <BarChart
        data={chartData}
        style={{
          background: grayPurple,
          borderRadius: 16,
        }}
        barSize={38}
        margin={{ top: 20 }}
      >
        <XAxis
          dataKey="dayName"
          orientation="bottom"
          scale="point"
          padding={{ left: 30, right: 30 }}
          tickLine={false}
          style={{ fontSize: '12px', color: grayBase }}
          axisLine={false}
        />
        <Tooltip content={renderTooltip} />
        <Legend content={renderLegend} />
        {activeKeys.map((key, index) => (
          <Bar
            key={key}
            dataKey={key}
            name={getSeriesName(key)}
            stackId="a"
            fill={keyColorMap[key]}
            isAnimationActive={false}
            radius={[3, 3, 3, 3]}
            stroke={white}
            strokeWidth={1}
            label={index === activeKeys.length - 1 ? renderCustomLabel : undefined}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};

export default memo(DataWeeklyChart);
